/**
 * Maps the displayed phase (same as the grid) to Email Triggers plugin ids and builds the API body
 * for POST /projects/:id/phase-trigger-email.
 */
import { isCvinExpansion } from "../projectGridColumns";
import { canonicalPhaseKey, phaseFlowForRow } from "../projectPhaseDisplay";
import { EMAIL_TRIGGER_DEFINITIONS } from "./emailTriggersStorage";

const PHASE_TO_TRIGGER_ID = {
  sales: "sales_complete",
  finance: "finance_complete",
  drafting: "drafting_complete",
  engineering: "drafting_complete",
  permitting: "permitting_complete",
  construction: "construction_complete",
  fiber: "fiber_complete",
  networking: "networking_complete",
  accounting: "accounting_complete",
};

const DEFINED_TRIGGER_IDS = new Set(EMAIL_TRIGGER_DEFINITIONS.map((d) => d.id));

/** Commercial Finance complete: narrow trigger when phase flow skips to Fiber or Networking. */
function financeCompleteTriggerIdForRow(row) {
  if (!row || isCvinExpansion(row)) return "finance_complete";
  const flow = phaseFlowForRow(row);
  const fi = flow.findIndex((p) => canonicalPhaseKey(p) === "finance");
  if (fi < 0 || fi >= flow.length - 1) return "finance_complete";
  const nextKey = canonicalPhaseKey(flow[fi + 1]);
  if (nextKey === "fiber") return "finance_complete_fiber_networking_only";
  if (nextKey === "networking") return "finance_complete_network_only";
  return "finance_complete";
}

/**
 * @param {string} phaseLabel e.g. displayPhaseForRow output
 * @returns {string|null}
 */
export function displayPhaseLabelToEmailTriggerId(phaseLabel) {
  const k = String(phaseLabel ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  const id = PHASE_TO_TRIGGER_ID[k];
  return id && DEFINED_TRIGGER_IDS.has(id) ? id : null;
}

/**
 * @param {string} phaseDisplayLabel phase being completed (before advance)
 * @param {object} [projectRow] when completing Finance on a commercial project, used to pick
 *   finance_complete vs finance_complete_fiber_networking_only vs finance_complete_network_only
 *   (same rules as {@link phaseFlowForRow} / department involvement).
 *   Expansion projects skip fiber_complete (no Networking handoff).
 * @returns {{ triggerId: string } | null}
 */
export function buildPhaseCompleteEmailPayload(phaseDisplayLabel, projectRow) {
  let triggerId = displayPhaseLabelToEmailTriggerId(phaseDisplayLabel);
  if (!triggerId) return null;
  if (triggerId === "finance_complete" && projectRow) {
    const narrowed = financeCompleteTriggerIdForRow(projectRow);
    if (narrowed !== "finance_complete") triggerId = narrowed;
  }
  // Expansion never involves Networking, so Fiber complete does not notify anyone.
  if (triggerId === "fiber_complete" && isCvinExpansion(projectRow)) return null;
  if (!DEFINED_TRIGGER_IDS.has(triggerId)) return null;
  return { triggerId };
}

/** User-visible note when the API skips or fails to send. */
export function describePhaseEmailResult(data, err) {
  if (err) {
    const st = err.response?.status;
    if (st === 404) {
      return "Phase email API is not registered. In backend/src/index.js, add before app.listen: mail.registerPhaseEmailRoutes(app, pool); — then restart the API.";
    }
    const apiMsg = err.response?.data?.error;
    return typeof apiMsg === "string" ? apiMsg : err.message || "Email could not be sent.";
  }
  if (!data || typeof data !== "object") return "";
  if (data.sent === true) return "";
  if (data.reason === "plugin_disabled") return "Email Triggers plugin is disabled.";
  if (data.reason === "no_recipients") {
    return "No recipients are configured for this trigger in Settings → Emails (or the selected roles no longer exist).";
  }
  if (data.reason === "expansion_skips_fiber_complete") return "";
  if (data.reason === "empty_template") return "Add a subject or body for this trigger in Settings → Emails.";
  if (data.reason === "no_smtp" || data.reason === "skipped_no_smtp") {
    return "SMTP is not configured on the server; notification was logged but not delivered.";
  }
  if (data.skipped) return "Email was not sent.";
  return "";
}
